import {
  Alert,
  Alignment,
  Direction,
  Displaying,
  FontWeight,
  Spacing,
  Typography,
  Button,
} from "@bonusx-global/bx-design-system";
import emojiRegex from "emoji-regex";
import type { AlertColor } from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { useAtomValue } from "jotai";
import useIsMobile from "../../../../hooks/device/useIsMobile";
import { resolveSurveyIcon } from "../../utils/resolve-survey-icon";
import { SurveyActionType, SurveyInputType } from "../../../../legacy/pages/survey/config/survey-input";
import type { SurveyCurrentStep } from "../../../../legacy/pages/survey/entities/survey-current-step";
import type { SurveyStepBodyExtra } from "../../../../legacy/pages/survey/entities/survey-step";
import { surveyIsFetchingDataAtom } from "../../../../legacy/pages/survey/stores/survey-store";

const CONTINUE_LABEL = "Continua";

const ALERT_EXTRA_COMPONENT = "alert";

const HEADER_ICON_SX = { fontSize: "42px" };

const RICH_TEXT_SX = { whiteSpace: "pre-line", "& b, & strong": { fontWeight: FontWeight.bold } };

function toAlertSeverity(variant: string | undefined): AlertColor {
  if (variant === "success" || variant === "warning" || variant === "error") {
    return variant;
  }
  return "info";
}

interface SurveyInfoScreenProps {
  step: SurveyCurrentStep;
}

function SurveyInfoScreen({ step }: Readonly<SurveyInfoScreenProps>): JSX.Element {
  const isMobile = useIsMobile();
  const isFetchingData = useAtomValue(surveyIsFetchingDataAtom);

  const isNextLoading = isFetchingData === SurveyActionType.NEXT;

  const marginX = isMobile ? Spacing["1500"] : "auto";
  const marginTop = isMobile ? Spacing["4000"] : Spacing["4500"];
  const marginBottom = Spacing["2500"];
  const maxWidth = isMobile ? "100%" : "608px";
  const minHeight = isMobile ? `calc(100vh - ${marginTop} - ${marginBottom})` : "auto";
  const alignItems = isMobile ? Alignment.stretch : Alignment.center;
  const textAlign = isMobile ? "left" : "center";
  const headerGap = isMobile ? Spacing["500"] : Spacing["1000"];
  const titleGap = isMobile ? Spacing["1500"] : Spacing["1000"];
  const footerGap = isMobile ? Spacing["3500"] : Spacing["4000"];

  function goToNextStep() {
    const payloadOfHiddenInputs = step.body.inputs.reduce(function getPayloadOfHiddenInputs(acc, input) {
      if (input.remoteInput.type === SurveyInputType.INPUT_HIDDEN) {
        const key = `${input.remoteInput.collectionKey}.${input.remoteInput.name}`;
        acc[key] = input.remoteInput.value;
      }
      return acc;
    }, {} as Record<string, unknown>);

    step.body.submit?.(payloadOfHiddenInputs);
  }

  function renderHeader() {
    const headerIcon = resolveSurveyIcon(step.header.title, HEADER_ICON_SX);
    if (headerIcon) {
      return headerIcon;
    }
    if (!step.header.title.trim()) {
      return null;
    }
    return (
      <Typography variant="h3" fontWeight={FontWeight.bold}>
        {step.header.title}
      </Typography>
    );
  }

  function renderExtraIcon(icon: string | undefined): JSX.Element | undefined {
    if (!icon) {
      return undefined;
    }
    const resolvedIcon = resolveSurveyIcon(icon);
    if (resolvedIcon) {
      return resolvedIcon;
    }
    const isLiteralEmoji = emojiRegex().test(icon);
    return isLiteralEmoji ? <span>{icon}</span> : undefined;
  }

  function renderExtra(extra: SurveyStepBodyExtra, extraIndex: number) {
    const isAlertComponent = extra.component === ALERT_EXTRA_COMPONENT;
    if (!isAlertComponent) {
      return null;
    }

    return (
      <Alert
        key={`${extra.component}-${extraIndex}`}
        severity={toAlertSeverity(extra.variant)}
        icon={renderExtraIcon(extra.icon)}
        sx={{ width: isMobile ? "100%" : "auto", alignItems: Alignment.center }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={RICH_TEXT_SX}
          dangerouslySetInnerHTML={{ __html: extra.description }}
        />
      </Alert>
    );
  }

  return (
    <Box
      display={Displaying.flex}
      flexDirection={Direction.column}
      justifyContent={Alignment.spaceBetween}
      marginX={marginX}
      marginTop={marginTop}
      marginBottom={marginBottom}
      maxWidth={maxWidth}
      minHeight={minHeight}
      gap={isMobile ? Spacing["3000"] : Spacing["4000"]}
      alignItems={alignItems}
      textAlign={textAlign}
    >
      <Box display={Displaying.flex} flexDirection={Direction.column} gap={headerGap} alignItems={alignItems}>
        {renderHeader()}

        <Box display={Displaying.flex} flexDirection={Direction.column} gap={titleGap}>
          <Typography variant={isMobile ? "h4" : "h3"} fontWeight={FontWeight.bold}>
            {step.body.title}
          </Typography>

          <Typography
            variant="body1"
            component="div"
            color="text.secondary"
            sx={RICH_TEXT_SX}
            dangerouslySetInnerHTML={{ __html: step.body.description }}
          />
        </Box>
      </Box>

      <Box
        display={Displaying.flex}
        flexDirection={Direction.column}
        gap={footerGap}
        width="100%"
        alignItems={alignItems}
      >
        {step.body.extra?.map(renderExtra)}

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth={isMobile}
          sx={isMobile ? undefined : { width: "256px" }}
          onClick={goToNextStep}
          loading={isNextLoading}
          disabled={isNextLoading}
        >
          {CONTINUE_LABEL}
        </Button>
      </Box>
    </Box>
  );
}

export default SurveyInfoScreen;
